import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, buildWhatsappUrl, track } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { X, Flame, Clock3, MessageCircle } from "lucide-react";
import InquiryDialog from "@/components/InquiryDialog";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "vjt.trending-popup";

function shouldShow(cfg) {
  if (!cfg?.enabled) return false;

  // Schedule check
  const today = new Date().toISOString().slice(0, 10);
  if (cfg.start_date && today < cfg.start_date) return false;
  if (cfg.end_date && today > cfg.end_date) return false;

  // Device targeting
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
  const dev = cfg.device_target || "both";
  if (dev === "desktop" && isMobile) return false;
  if (dev === "mobile" && !isMobile) return false;

  // Frequency
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.dismissedAt) {
      const freq = cfg.frequency || "session";
      if (freq === "session") {
        const s = sessionStorage.getItem(STORAGE_KEY);
        if (s === "dismissed") return false;
      } else if (freq === "daily") {
        const dt = new Date(parsed.dismissedAt);
        const diff = (Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24);
        if (diff < 1) return false;
      } else if (freq === "always") {
        // show every visit
      }
    }
  } catch (_) { /* ignore */ }

  return true;
}

function markDismissed(freq) {
  try {
    if (freq === "session") sessionStorage.setItem(STORAGE_KEY, "dismissed");
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissedAt: new Date().toISOString() }));
  } catch (_) { /* ignore */ }
}

export default function TrendingPopup() {
  const { pathname } = useLocation();
  const { settings } = useSettings();
  const { data: cfg } = useQuery({
    queryKey: ["trending-popup"],
    queryFn: () => api.get("/trending-popup").then((r) => r.data),
    staleTime: 60_000,
  });
  const [open, setOpen] = useState(false);

  // Page targeting: home only or all pages
  const pageOk = (cfg?.page_target || "home") === "home" ? pathname === "/" : true;

  useEffect(() => {
    if (!cfg || !pageOk) return;
    if (!shouldShow(cfg)) return;
    const delay = Number(cfg.delay_seconds ?? 3) * 1000;
    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, [cfg, pageOk]);

  if (!open || !cfg?.package) return null;

  const pkg = cfg.package;
  const badge = cfg.badge_text || "Trending Package";
  const title = cfg.custom_title || pkg.name;
  const description = cfg.custom_description || pkg.overview;
  const image = cfg.custom_image_url || pkg.banner_url || pkg.gallery?.[0];
  const primaryLabel = cfg.primary_cta_label || "Book Now";
  const secondaryLabel = cfg.secondary_cta_label;
  const wa = settings?.whatsapp_number || "+919866933180";

  const close = () => {
    markDismissed(cfg.frequency || "session");
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-3 sm:p-6" data-testid="trending-popup">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden shadow-2xl animate-fade-up">
        <button
          onClick={close}
          data-testid="trending-popup-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 grid place-items-center shadow"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-slate-700" />
        </button>

        <div className="grid md:grid-cols-2">
          {image && (
            <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[360px] overflow-hidden">
              <img src={image} alt={title} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-amber-400 text-slate-950 rounded-full px-3 py-1 text-xs font-bold shadow">
                <Flame className="w-3.5 h-3.5" /> {badge}
              </div>
            </div>
          )}

          <div className="p-6 md:p-8 flex flex-col">
            <div className="text-[11px] uppercase tracking-[0.24em] text-amber-600">
              {pkg.destination} · {pkg.duration_days}D / {pkg.duration_nights}N
            </div>
            <h3 className="mt-2 font-display text-3xl md:text-4xl text-slate-950 leading-tight">{title}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed line-clamp-4">{description}</p>

            {pkg.highlights?.length > 0 && (
              <ul className="mt-4 space-y-1.5">
                {pkg.highlights.slice(0, 3).map((h, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2" />
                    {h}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-5 flex items-baseline gap-2">
              <div className="text-xs text-slate-500">Starting from</div>
              <div className="font-display text-2xl text-brand-navy-900">₹{pkg.price_from?.toLocaleString?.("en-IN")}</div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-5">
              {primaryLabel.toLowerCase().includes("book") || primaryLabel.toLowerCase().includes("enquire") ? (
                <InquiryDialog
                  defaultMessage={`Hello! I'm interested in the ${title} trending package.`}
                  sourceRef={pkg.slug}
                  source="trending_popup"
                  triggerTestId="trending-popup-primary"
                >
                  <Button
                    className="rounded-full bg-brand-navy-900 hover:bg-brand-navy-800 text-white px-6"
                    data-testid="trending-popup-primary"
                    onClick={() => track("whatsapp_click", "trending_popup_primary")}
                  >
                    {primaryLabel}
                  </Button>
                </InquiryDialog>
              ) : (
                <Link to={`/packages/${pkg.slug}`} onClick={close}>
                  <Button className="rounded-full bg-brand-navy-900 hover:bg-brand-navy-800 text-white px-6" data-testid="trending-popup-primary">
                    {primaryLabel}
                  </Button>
                </Link>
              )}

              {secondaryLabel && secondaryLabel !== "none" && (
                secondaryLabel.toLowerCase().includes("contact") ? (
                  <Link to="/contact" onClick={close}>
                    <Button variant="outline" className="rounded-full" data-testid="trending-popup-secondary">{secondaryLabel}</Button>
                  </Link>
                ) : (
                  <Link to={`/packages/${pkg.slug}`} onClick={close}>
                    <Button variant="outline" className="rounded-full" data-testid="trending-popup-secondary">{secondaryLabel}</Button>
                  </Link>
                )
              )}

              <a
                href={buildWhatsappUrl(wa, `Hi! I'm interested in the ${title} package.`)}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("whatsapp_click", "trending_popup_wa")}
                className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white text-sm font-medium px-4 py-2 hover:bg-[#1ebe57]"
                data-testid="trending-popup-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
